/**
 * US + DC standard-plate map fills. Images live in public/assets/us-plates/{fips}.jpg.
 * The plate is scaled uniformly to cover the state (preserveAspectRatio slice) and
 * clipped by the state path, so a small state and a large one both show the whole design.
 */

export const US_PLATE_STATE_IDS = [
	'01', '02', '04', '05', '06', '08', '09', '10', '11', '12', '13', '15', '16', '17',
	'18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31',
	'32', '33', '34', '35', '36', '37', '38', '39', '40', '41', '42', '44', '45', '46',
	'47', '48', '49', '50', '51', '53', '54', '55', '56',
];

const US_PLATE_STATE_ID_SET = new Set(US_PLATE_STATE_IDS);

export function hasPlateArt(stateId) {
	return US_PLATE_STATE_ID_SET.has(String(stateId || ''));
}

export function isUsPlateMap(mapType) {
	return mapType === 'US' || mapType === 'COMBINED';
}

export function plateArtAssetUrl(stateId) {
	const base = process.env.PUBLIC_URL || '';
	return `${base}/assets/us-plates/${stateId}.jpg`;
}

/** @returns {'orange'|'green'|null} null means the state has not been seen */
export function plateArtTintName(tier, playMode) {
	if (tier === 'current_month') return playMode === 'monthly' ? 'orange' : 'green';
	if (tier === 'window_only') return 'orange';
	return null;
}

export function plateArtPatternId(stateId, tint) {
	return `platechase-plate-${stateId}-${tint}`;
}

export function plateArtClipId(stateId) {
	return `platechase-plate-clip-${stateId}`;
}

/**
 * Stronger wash than a flat color fill. The plate stays visible, and the
 * thick inner outline (see plateArtStroke) carries most of the hue.
 */
export const PLATE_ART_TINTS = {
	orange: { color: '#FF5722', opacity: 0.38 },
	green: { color: '#2E7D32', opacity: 0.42 },
};

/** Inner outline, clipped to the state so it does not spill into a neighbor. */
const STROKE_COLOR = {
	orange: '#FF5722',
	green: '#1B5E20',
};
const MIN_INSET = 1.21875;
const MAX_INSET = 4.125;

/**
 * Stroke width for an inner border. Half of this width is visible inside the
 * state. Small states keep a plate center; large states get a thick frame.
 * @param {string} svgPath
 */
export function plateArtInnerStrokeWidth(svgPath) {
	const nums = String(svgPath || '').match(/-?\d*\.?\d+(?:e[-+]?\d+)?/gi);
	if (!nums || nums.length < 4) return MIN_INSET * 2;
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (let i = 0; i + 1 < nums.length; i += 2) {
		const x = Number(nums[i]);
		const y = Number(nums[i + 1]);
		if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
		if (x < minX) minX = x;
		if (y < minY) minY = y;
		if (x > maxX) maxX = x;
		if (y > maxY) maxY = y;
	}
	const minSide = Math.min(maxX - minX, maxY - minY);
	if (!Number.isFinite(minSide) || minSide <= 0) return MIN_INSET * 2;
	const inset = Math.min(MAX_INSET, Math.max(MIN_INSET, minSide * 0.06));
	return inset * 2;
}

/** @returns {{ color: string, width: number }|null} */
export function plateArtStroke(tint, svgPath) {
	const color = STROKE_COLOR[tint];
	if (!color) return null;
	return { color, width: plateArtInnerStrokeWidth(svgPath) };
}
